import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import {
  Attachment,
  Contract,
  ContractGroup,
  ClientContracts,
  ContractAdvice,
  ContractResponse,
  ContractOverviewRequest,
  ContractOverviewResponse,
  ContractOverviewCriteria,
  DetectedContractsResponseModel,
  BatchContractTransferResponseModel,
} from '../model';

@Injectable({
  providedIn: 'root',
})
export class ContractsManagerService {
  public isOtherContractOpened: boolean = false;

  constructor(private http: HttpClient) {}

  public load(contractUid: string): Observable<ContractResponse> {
    return this.http.get<ContractResponse>(`${environment.apiEndpoint}/b2b/contractsmanager/load/${contractUid}`);
  }

  public loadContractAttachments(contractUid: string): Observable<any> {
    return this.http.get<any>(`${environment.apiEndpoint}/b2b/contractsattachment/${contractUid}`);
  }

  /** all documents across all contracts of a client (client details → contract documents overview) */
  public loadClientContractAttachments(clientId: string): Observable<Array<Attachment>> {
    return this.http.get<Array<Attachment>>(`${environment.apiEndpoint}/b2b/contractsattachment/client/${clientId}`);
  }

  /** includeDetected=false leaves out contracts that are already linked to a detected contract (merge picker) */
  public loadClientContracts(clientUid: string, includeDetected?: boolean): Observable<ClientContracts> {
    const params: Record<string, boolean> = includeDetected === undefined ? {} : { includeDetected };

    return this.http.get<ClientContracts>(
      `${environment.apiEndpoint}/b2b/contractsmanager/clientcontracts/${clientUid}`,
      { params },
    );
  }

  public contractGroups(): Observable<Array<ContractGroup>> {
    return this.http.get<Array<ContractGroup>>(`${environment.apiEndpoint}/b2b/contractsmanager/contractgroups`);
  }

  public saveAdvice(contractAdvice: ContractAdvice): Observable<Contract> {
    return this.http.post<Contract>(`${environment.apiEndpoint}/b2b/rating/advice`, contractAdvice);
  }

  public saveContract(contract: Contract): Observable<Contract> {
    return this.http.post<Contract>(`${environment.apiEndpoint}/b2b/contractsmanager/save`, contract);
  }

  public removeContract(contractUid: string): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/b2b/contractsmanager/archive/${contractUid}`);
  }

  public deleteContract(contractUid: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/b2b/contractsmanager/delete/${contractUid}`);
  }

  public getLastCreatedContracts(size: number): Observable<Contract[]> {
    return this.http.get<Contract[]>(`${environment?.apiEndpoint}/b2b/contractsmanager/advisorcontracts?size=${size}`);
  }

  // get all contracts for simple admin
  public getAllContract(body: ContractOverviewRequest): Observable<ContractOverviewResponse> {
    return this.http.post<ContractOverviewResponse>(`${environment?.apiEndpoint}/b2b/advisor/users/contracts`, body);
  }

  // get all contracts for company admin
  public getAllContractByAdvisorId(
    advisorId: string,
    body: ContractOverviewRequest,
  ): Observable<ContractOverviewResponse> {
    return this.http.post<ContractOverviewResponse>(
      `${environment?.apiEndpoint}/b2b/advisor/${advisorId}/users/contracts`,
      body,
    );
  }

  // download scv by request param
  public getCSVByCompanyId(companyId: number, body: ContractOverviewCriteria[]): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/csv',
    });

    return this.http.post<Blob>(
      `${environment?.apiEndpoint}/b2b/company/${companyId}/advisor/users/contracts/export`,
      body,
      { headers, responseType: 'blob' as 'json' },
    );
  }

  public getCSVByByAdvisorId(advisorId: string, body: ContractOverviewCriteria[]): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/csv',
    });

    return this.http.post<Blob>(`${environment?.apiEndpoint}/b2b/advisor/${advisorId}/users/contracts/export`, body, {
      headers,
      responseType: 'blob' as 'json',
    });
  }

  public getDetectedContracts(uid: string): Observable<DetectedContractsResponseModel> {
    return this.http.get<DetectedContractsResponseModel>(
      `${environment?.apiEndpoint}/b2b/contractsmanager/v2/detectedcontracts/${uid}`,
    );
  }

  /** cannot be undone, the client no longer sees the detected contract either */
  public ignoreDetectedContract(clientUid: string, detectedContractId: string | number): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiEndpoint}/b2b/contractsmanager/detectedcontracts/${clientUid}/${detectedContractId}/ignore`,
      {},
    );
  }

  public trigger(contractUid: string, poolIdentification: string, bearerToken: string): Observable<void> {
    return this.http.get<any>(`${environment.apiEndpoint}/b2b/contractsmanager/transfer/${contractUid}`, {
      params: {
        poolIdentification,
        bearerToken,
      },
    });
  }

  public batchTransfer(body: {
    contractUids: string[];
    poolIdentification: string;
    bearerToken: string;
  }): Observable<BatchContractTransferResponseModel> {
    return this.http.post<BatchContractTransferResponseModel>(
      `${environment.apiEndpoint}/b2b/contractsmanager/transfer/batch`,
      body,
    );
  }
}
